import { Modal } from 'antd';
import ButtonWithSpinner from 'components/common/ButtonWithSpinner';
import DefaultAvatar from 'components/common/DefaultAvatar';
import IconComponent from 'components/common/IconComponent';
import deviceSizes from 'constants/deviceSizes';
import { useChatRoomData } from 'hooks/useMessagesRelatedProps';
import useWindowSize from 'hooks/useWindowSize';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import SelectedAppointmentContactAction from 'redux/selected-contact/selected-contact.action';
import SelectedAppointmentContactSelectors from 'redux/selected-contact/selected-contact.selectors';
import { GeneralFunction } from 'types/common';
import { IChatRoom } from 'types/messages';

import ChatBoxConversation from './ChatBoxConversation';
import ChatRoomList from './ChatRoomList';

const ChatBoxComponent: React.FC<{
  onChatTrigger: GeneralFunction;
  refetchChatRoomData: GeneralFunction;
  chatRoomData?: IChatRoom[];
  loading?: boolean;
}> = ({ chatRoomData, refetchChatRoomData, onChatTrigger, loading }) => (
  <div className='chatbox'>
    <div className='chatbox-header'>
      <div>Chat</div>
      <div>
        <ButtonWithSpinner height='auto' width='auto' onClick={onChatTrigger} buttonType='text'>
          <div>
            <IconComponent name='chevron-down' />
          </div>
        </ButtonWithSpinner>
      </div>
    </div>
    <div className='chatbox-content'>
      <div className='chatbox-conversation'>
        <ChatBoxConversation loading={loading} refetchChatRoomData={refetchChatRoomData} />
      </div>
      <div className='chatbox-conversation-list'>
        <ChatRoomList refetchChatRoomData={refetchChatRoomData} chatRooms={chatRoomData} />
      </div>
    </div>
  </div>
);

const ChatBox: React.FC = () => {
  const { isMobile } = useWindowSize(deviceSizes.numbers.sm);
  const dispatch = useDispatch();
  const selectedAppointmentContact = useSelector(SelectedAppointmentContactSelectors.selectAppointmentContact);
  const selectedAppointmentContactId = selectedAppointmentContact.id;

  const {
    chatRoomData,
    // chatRoomLoading,
    refetchChatRoomData,
    totalUnreadMessages,
    subscribeToMessagesForUpdateChatRoom,
  } = useChatRoomData();

  const isChatboxActive = selectedAppointmentContact.active;
  const isChatConversationSelected = selectedAppointmentContactId;

  useEffect(() => {
    const unsubMessage = subscribeToMessagesForUpdateChatRoom();
    return () => {
      unsubMessage();
    };
  }, []);

  const onChatTrigger = () => {
    dispatch(
      SelectedAppointmentContactAction.setSelectedAppointmentContact({ active: !selectedAppointmentContact.active }),
    );
  };

  useEffect(() => {
    if (selectedAppointmentContact.chatRoom) {
      const newChatRoom = chatRoomData.find((cR) => cR.id === selectedAppointmentContactId);
      if (newChatRoom)
        dispatch(SelectedAppointmentContactAction.setSelectedAppointmentContact({ chatRoom: newChatRoom }));
    }
  }, [chatRoomData]);

  return (
    <>
      <div className={!isMobile && isChatboxActive ? undefined : 'gx-d-none'}>
        <ChatBoxComponent
          refetchChatRoomData={refetchChatRoomData}
          chatRoomData={chatRoomData}
          onChatTrigger={onChatTrigger}
          // fetchMoreMessage={fetchMoreMessage}
        />
      </div>
      <Modal
        wrapClassName='gx-overflow-y-hidden'
        className={`chatbox-modal ${isChatConversationSelected ? 'conversation-active' : ''}`}
        visible={isChatboxActive && isMobile}
        title={null}
        closable={false}
        footer={null}>
        <ChatBoxComponent
          refetchChatRoomData={refetchChatRoomData}
          chatRoomData={chatRoomData}
          onChatTrigger={onChatTrigger}
        />
      </Modal>
      <div className={`chat-footer-button ${!isMobile && !isChatboxActive ? '' : 'gx-d-none'}`}>
        <ButtonWithSpinner width='100%' rounded={false} onClick={onChatTrigger}>
          <div className='gx-d-flex gx-align-items-center'>
            <IconComponent
              style={{ transform: 'rotateY(180deg)' }}
              className='gx-mr-1'
              name='chat'
              color='038fde'
              size={13}
            />
            <span>Chat</span>
          </div>
        </ButtonWithSpinner>
        {!!totalUnreadMessages && (
          <div className='unread-message-count'>
            <DefaultAvatar bgColor='red'>{totalUnreadMessages}</DefaultAvatar>
          </div>
        )}
      </div>
    </>
  );
};

export default ChatBox;
